// src/lib/brand-logo.js
//
// SHARED, ENV-FREE single source of the HeliGreece SVG brand mark (Welle 1:
// the SVG that shipped admin-only is now product-wide across the WEB surfaces —
// customer navbar/footer, SSR site-chrome, operator portal, token/print pages).
//
// This module reads NO env / siteBaseUrl / import.meta — only two build-time
// `?raw` SVG imports — so it is safe to import from the CLIENT bundle (App.jsx)
// as well as from SSR. The admin builders (src/lib/admin/branding.js) are now
// thin re-exports of this module, so the admin chrome + its byte-identity guards
// (scenario-275) are unchanged.
//
// EMAILS deliberately do NOT use this: inline <svg> does not render in Gmail /
// Outlook, so transactional/marketing mail keeps the text wordmark via
// src/lib/brand.js `logoHtml` / `wordmarkHtml` (untouched, byte-identical).
import wordmarkRaw from '../assets/brand/logo-wordmark-light.svg?raw';
import fullRaw from '../assets/brand/logo-light.svg?raw';

// Inner markup only — strip the source file's own <svg> wrapper so THIS module
// controls the root attributes (size + a11y). Everything inside (paths/defs) is
// preserved verbatim in the file's own viewBox coordinate system.
function innerMarkup(raw) {
  return String(raw || '')
    .replace(/^[\s\S]*?<svg[^>]*>/i, '')
    .replace(/<\/svg>\s*$/i, '')
    .trim();
}

function viewBoxOf(raw) {
  const m = String(raw || '').match(/viewBox\s*=\s*"([^"]+)"/i);
  return m ? m[1].trim() : '0 0 1 1';
}

// width = height x (viewBox width / viewBox height) — keeps the exact aspect
// ratio and sets both attributes so the box is reserved before paint (no CLS).
function widthFor(viewBox, height) {
  const p = viewBox.split(/\s+/).map(Number);
  const ratio = (p[2] > 0 && p[3] > 0) ? p[2] / p[3] : 1;
  return Math.round(height * ratio);
}

function renderSvg(raw, { height, label }) {
  const viewBox = viewBoxOf(raw);
  const width = widthFor(viewBox, height);
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + viewBox + '"'
    + ' width="' + width + '" height="' + height + '"'
    + ' role="img" aria-label="' + label + '" focusable="false"'
    + ' style="display:block;height:' + height + 'px;width:' + width + 'px">'
    + innerMarkup(raw)
    + '</svg>';
}

// Top-bar wordmark (no tagline). viewBox 3.23 1.59 229.9 34.91 → 6.586:1.
// Default 24px tall → 158px wide. Output is byte-identical to the prior
// admin builder (adminWordmarkSvg) so scenario-275 stays green.
export function wordmarkSvg({ height = 24 } = {}) {
  return renderSvg(wordmarkRaw, { height, label: 'HeliGreece' });
}

// Full logo (with tagline). viewBox 0 0 234 54 → 4.333:1. Default 50px → 217px.
export function fullLogoSvg({ height = 50 } = {}) {
  return renderSvg(fullRaw, { height, label: 'HeliGreece — Private Helicopter Charters' });
}

// Dark-background variant of the wordmark: swap the two light-mode fills
// (#10243A dark ink, #0D56A6 brand blue) for footer-legible tones (white +
// #B8D0F0 = --blmid). These are the EXACT colours the prior text-wordmark
// footer used, so the dark footer stays legible and visually unchanged. The
// two hexes appear only as path fills, so a global swap is deterministic.
export function wordmarkSvgOnDark({ height = 24 } = {}) {
  return wordmarkSvg({ height })
    .replace(/#10243A/gi, '#FFFFFF')
    .replace(/#0D56A6/gi, '#B8D0F0');
}

// Dark-background variant of the FULL logo (with tagline). Swaps the three
// light-mode fills for dark-legible tones: #10243A ink -> white, #0D56A6 accent
// -> #B8D0F0 (--blmid), and the #8296A9 tagline grey -> #B8C4D0 (a light
// blue-grey that reads as secondary on dark yet stays legible). Only for the
// footer / dark surfaces.
export function fullLogoSvgOnDark({ height = 50 } = {}) {
  return fullLogoSvg({ height })
    .replace(/#10243A/gi, '#FFFFFF')
    .replace(/#0D56A6/gi, '#B8D0F0')
    .replace(/#8296A9/gi, '#B8C4D0');
}
